import React, { useMemo, useState } from 'react';
import { Badge, Box, Flex, SimpleGrid, Spinner, Stack, Text } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  Archive,
  CheckCircle,
  CircleWavyCheck,
  ClockClockwise,
  HandPalm,
  HourglassMedium,
  Icon,
  MapPin,
  Robot,
  WarningCircle,
} from 'phosphor-react';
import { GlassCard } from '../../../../components/glass';
import { SlaBadge, SlaInfo } from '../../../../components/kanban/SlaBadge';
import { useKanban } from '../../../../hooks/useKanban';
import {
  ILegalizacao,
  TipoLegalizacao,
  TIPOS_LEGALIZACAO,
  TIPO_LEGALIZACAO_LABEL,
  useLegalizacao,
} from '../../../../hooks/useLegalizacao';
import {
  COR_TIPO,
  fluxoDaEtapa,
  nivelRisco,
  NivelRisco,
  proximaAcao,
  QuemDeve,
  QUEM_META,
  regimeDoProcesso,
  RISCO_META,
  tiposAlteracaoDoProcesso,
} from '../legalizacaoPlaybook';

const MotionDiv = motion.div;

// Os rótulos de QUEM_META são minúsculos porque no card vivem dentro de um badge em
// caixa alta ("VOCÊ"). Em lista, caixa de frase.
const capitalizar = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

// Registro achatado por processo (leg = fonte da etapa/junta; card = SLA/título).
interface Registro {
  id: number;
  tipo: TipoLegalizacao;
  coluna: string;
  junta: string | null;
  titulo: string;
  sla: SlaInfo | null;
  /** Onde está a pendência — mesma `proximaAcao` que o card do quadro imprime. */
  quem: QuemDeve;
}

// KPI card grande (gramática ContaCil): chip de ícone + número + rótulo.
const KpiCard: React.FC<{
  valor: number;
  label: string;
  accent: string;
  icon: Icon;
  delay: number;
  /** Ressalva que qualifica o número — ex.: "0 em risco" com 8 sem prazo medido. */
  nota?: string;
}> = ({ valor, label, accent, icon: IconComp, delay, nota }) => (
  <MotionDiv
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay, ease: [0.4, 0, 0.2, 1] }}
  >
    <GlassCard intensity="medium" borderRadius="2xl" p={4} h="full">
      <Flex align="center" justify="space-between" mb={2}>
        <Text
          fontSize="10px"
          fontWeight="700"
          color="gray.700"
          textTransform="uppercase"
          letterSpacing="0.06em"
        >
          {label}
        </Text>
        <Flex
          w="32px"
          h="32px"
          borderRadius="lg"
          align="center"
          justify="center"
          bg={`${accent}14`}
          color={accent}
        >
          <IconComp size={18} weight="duotone" />
        </Flex>
      </Flex>
      <Text fontSize="28px" fontWeight="700" color={accent} lineHeight="1" letterSpacing="-0.02em">
        {valor}
      </Text>
      {nota && (
        <Text fontSize="11px" color="gray.500" mt={1.5} lineHeight="1.3">
          {nota}
        </Text>
      )}
    </GlassCard>
  </MotionDiv>
);

interface Props {
  /**
   * Abre o processo no quadro (troca de aba + abre o modal). Sem isto a lista
   * "Em risco agora" é um beco sem saída: mostra o que está pegando fogo e
   * obriga o operador a procurar o card na mão.
   */
  onAbrirProcesso?: (legId: number, tipo: TipoLegalizacao) => void;
}

export const LegalizacaoVisaoGeral: React.FC<Props> = ({ onAbrirProcesso }) => {
  const { getProjects, getBoard, getCardsArquivados } = useKanban();
  const { listarLegalizacoes } = useLegalizacao();
  const [tipoFunil, setTipoFunil] = useState<TipoLegalizacao>('constituicao');

  const { data: projectsData, isLoading: loadingProj } = useQuery({
    queryKey: ['kanban-projects'],
    queryFn: getProjects,
    staleTime: 30_000,
  });
  const projetoId = (t: TipoLegalizacao) =>
    (projectsData?.data ?? []).find(p => p.origem_modulo === `lgz_${t}`)?.id;

  const boardConst = useQuery({
    queryKey: ['kanban-board', projetoId('constituicao')],
    queryFn: () => getBoard(projetoId('constituicao')),
    enabled: !!projetoId('constituicao'),
    staleTime: 30_000,
  });
  const boardAlt = useQuery({
    queryKey: ['kanban-board', projetoId('alteracao')],
    queryFn: () => getBoard(projetoId('alteracao')),
    enabled: !!projetoId('alteracao'),
    staleTime: 30_000,
  });
  const boardBaixa = useQuery({
    queryKey: ['kanban-board', projetoId('baixa')],
    queryFn: () => getBoard(projetoId('baixa')),
    enabled: !!projetoId('baixa'),
    staleTime: 30_000,
  });
  const { data: legsData, isLoading: loadingLegs } = useQuery({
    queryKey: ['legalizacoes', 'todas'],
    queryFn: () => listarLegalizacoes(),
    staleTime: 30_000,
  });

  // Arquivados de verdade, da MESMA fonte do chip "Arquivados (N)" do quadro —
  // mesma queryKey, então o cache é compartilhado e navegar entre quadro e visão
  // geral não refaz request. Derivar do board ("tem card e não está no quadro")
  // contaria também card excluído logicamente, e os dois números divergiriam.
  const arqConst = useQuery({
    queryKey: ['legalizacao-arquivados', projetoId('constituicao')],
    queryFn: () => getCardsArquivados(projetoId('constituicao') as number),
    enabled: !!projetoId('constituicao'),
    staleTime: 30_000,
  });
  const arqAlt = useQuery({
    queryKey: ['legalizacao-arquivados', projetoId('alteracao')],
    queryFn: () => getCardsArquivados(projetoId('alteracao') as number),
    enabled: !!projetoId('alteracao'),
    staleTime: 30_000,
  });
  const arqBaixa = useQuery({
    queryKey: ['legalizacao-arquivados', projetoId('baixa')],
    queryFn: () => getCardsArquivados(projetoId('baixa') as number),
    enabled: !!projetoId('baixa'),
    staleTime: 30_000,
  });

  const boardsPorTipo: Record<TipoLegalizacao, typeof boardConst> = {
    constituicao: boardConst,
    alteracao: boardAlt,
    baixa: boardBaixa,
  };

  // Ordem das colunas por tipo (do board) + mapa cardId → {sla, título}.
  const { colunasPorTipo, cardMap } = useMemo(() => {
    const colunas = {} as Record<TipoLegalizacao, string[]>;
    const map = new Map<number, { sla: SlaInfo | null; titulo: string }>();
    TIPOS_LEGALIZACAO.forEach(t => {
      const cols = boardsPorTipo[t].data?.data?.columns ?? [];
      colunas[t] = cols.map(c => c.titulo);
      cols.forEach(col =>
        (col.cards ?? []).forEach(card =>
          map.set(card.id, { sla: card.sla ?? null, titulo: card.titulo }),
        ),
      );
    });
    return { colunasPorTipo: colunas, cardMap: map };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardConst.data, boardAlt.data, boardBaixa.data]);

  // O board DAQUELE tipo respondeu com sucesso? Tem que ser `isSuccess` e por
  // tipo, não `!isLoading` global: `isLoading` é `isPending && isFetching`, ou
  // seja `false` tanto para query que ERROU quanto para query DESABILITADA
  // (`enabled: false` quando o projeto `lgz_<tipo>` ainda não existe). Com o
  // gate global, um board que falhasse deixaria o `cardMap` sem os cards dele e
  // o filtro abaixo apagaria TODOS os processos daquele tipo dos KPIs/funil,
  // contando-os como arquivados. Por tipo, o pior caso é não filtrar.
  const boardOkPorTipo: Record<TipoLegalizacao, boolean> = {
    constituicao: boardConst.isSuccess,
    alteracao: boardAlt.isSuccess,
    baixa: boardBaixa.isSuccess,
  };
  const tipoDaLeg = (leg: ILegalizacao): TipoLegalizacao =>
    (leg.tipo_legalizacao as TipoLegalizacao) ?? 'constituicao';

  // Um registro por legalização (a etapa vem do leg.status; SLA/título do card).
  //
  // ARQUIVADOS NÃO CONTAM: o board já exclui cards com `arquivado_em`, então um
  // processo cujo card sumiu do `cardMap` está arquivado (ou o card foi excluído)
  // — em qualquer dos casos não é trabalho em andamento e não deve aparecer nos
  // KPIs/funil. Processo SEM card (`kanban_card_id == null`) é outra coisa: a
  // criação do card é best-effort (try/except no controller), então ele existe e
  // continua contando.
  const registros: Registro[] = useMemo(() => {
    return (legsData?.data ?? [])
      .filter((leg: ILegalizacao) => {
        // Finalizado não é trabalho em andamento: entra só em "Concluídos" e no funil.
        if (leg.finalizado_em) return false;
        if (leg.kanban_card_id == null) return true; // sem card ≠ arquivado
        // Board do tipo não respondeu (carregando, erro ou sem projeto): não dá
        // pra afirmar que sumiu do quadro. Não filtra — degradar é melhor que
        // fazer o processo desaparecer da tela sem aviso.
        if (!boardOkPorTipo[tipoDaLeg(leg)]) return true;
        return cardMap.has(leg.kanban_card_id);
      })
      .map((leg: ILegalizacao) => {
        const card = leg.kanban_card_id != null ? cardMap.get(leg.kanban_card_id) : undefined;
        const coluna = leg.status ?? '';
        // MESMA chamada que o card do quadro faz (LegalizacaoBoard) — inclusive o
        // `opts`, que muda o checklist efetivo em algumas UFs. Recalcular por conta
        // própria abriria a porta pro painel discordar do que está escrito no card.
        const estado =
          (leg.estado_etapas as Record<string, Record<string, unknown>> | null)?.[coluna] ?? {};
        return {
          id: leg.id,
          tipo: tipoDaLeg(leg),
          coluna,
          junta: leg.junta_uf,
          titulo: card?.titulo ?? `Processo #${leg.id}`,
          sla: card?.sla ?? null,
          quem: proximaAcao(tipoDaLeg(leg), coluna, estado, {
            uf: leg.junta_uf,
            municipio: leg.municipio,
            tiposAlteracao: tiposAlteracaoDoProcesso(leg.estado_etapas),
            regime: regimeDoProcesso(leg.estado_etapas),
          }).quem,
        };
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legsData, cardMap, boardConst.isSuccess, boardAlt.isSuccess, boardBaixa.isSuccess]);

  // Buckets cross-tipo (disjuntos) + em risco (transversal).
  //
  // O 4º KPI é um ROLL-UP dos níveis 2 e 3 da mesma régua (`nivelRisco`), e não um
  // contador próprio. Antes ele somava crítico + atrasado sob o rótulo "Atrasados",
  // enquanto o painel logo abaixo separava os dois: o operador via "Atrasados 5" em
  // cima e "Crítico 3 · Atrasados 2" embaixo, na mesma tela, sem explicação. Agora
  // o número de cima DECOMPÕE no painel de baixo (2+3 = 5) e casa exatamente com o
  // tamanho da lista "Em risco agora" — três elementos dizendo a mesma coisa.
  /**
   * O processo está na última etapa do tipo? Ali ele já foi entregue: o semáforo compara o
   * prazo com hoje e acusaria atraso até de quem entregou no prazo — não é risco.
   */
  const naUltimaEtapa = (r: Registro) => {
    const cols = colunasPorTipo[r.tipo] ?? [];
    return !!r.coluna && r.coluna === cols[cols.length - 1];
  };

  // Finalizados por tipo (fora do quadro) e os cards deles — o "Finalizar" arquiva o card,
  // então a lista de arquivados vem com eles e precisa ser descontada.
  const { finalizadosPorTipo, cardsFinalizados } = useMemo(() => {
    const porTipo: Record<TipoLegalizacao, number> = { constituicao: 0, alteracao: 0, baixa: 0 };
    const cards = new Set<number>();
    (legsData?.data ?? []).forEach(l => {
      if (!l.finalizado_em) return;
      porTipo[(l.tipo_legalizacao as TipoLegalizacao) ?? 'constituicao'] += 1;
      if (l.kanban_card_id != null) cards.add(l.kanban_card_id);
    });
    return { finalizadosPorTipo: porTipo, cardsFinalizados: cards };
  }, [legsData]);

  const kpis = useMemo(() => {
    let andamento = 0;
    let aguardando = 0;
    let concluidos = 0;
    let emRisco = 0;
    // Quantos dos "em etapa de espera" ainda têm pendência do operador. É o que explica
    // o KPI de espera conviver com "Órgão 0" no painel de bola, em vez de parecer erro.
    let esperaComPendenciaPropria = 0;
    registros.forEach(r => {
      const naUltima = naUltimaEtapa(r);
      if (naUltima) concluidos += 1;
      else if (fluxoDaEtapa(r.tipo, r.coluna).includes('wait')) {
        aguardando += 1;
        if (r.quem === 'equipe') esperaComPendenciaPropria += 1;
      } else andamento += 1;
      if (!naUltima && nivelRisco(r.sla) >= 2) emRisco += 1;
    });
    concluidos += TIPOS_LEGALIZACAO.reduce((n, t) => n + finalizadosPorTipo[t], 0);
    return { andamento, aguardando, concluidos, emRisco, esperaComPendenciaPropria };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registros, colunasPorTipo, finalizadosPorTipo]);

  // Arquivados por tipo — contagem da própria lista de arquivados, não derivada
  // do board. "Tem card e não está no quadro" também pegaria card excluído
  // logicamente (que a lista de arquivados exclui), e aí este número e o do chip
  // "Arquivados (N)" do quadro divergiriam sem explicação.
  const arquivados = (lista?: { id: number }[]) =>
    (lista ?? []).filter(c => !cardsFinalizados.has(c.id)).length;
  const arquivadosPorTipo: Record<TipoLegalizacao, number> = {
    constituicao: arquivados(arqConst.data?.data),
    alteracao: arquivados(arqAlt.data?.data),
    baixa: arquivados(arqBaixa.data?.data),
  };
  // Linhas "fora do quadro" do funil, só as que têm alguém.
  const foraDoQuadro = [
    {
      rotulo: 'Finalizados',
      n: finalizadosPorTipo[tipoFunil],
      icone: <CircleWavyCheck size={13} weight="duotone" />,
    },
    {
      rotulo: 'Arquivados',
      n: arquivadosPorTipo[tipoFunil],
      icone: <Archive size={13} weight="duotone" />,
    },
  ].filter(l => l.n > 0);

  // Funil do tipo selecionado: contagem por etapa na ordem das colunas.
  const funil = useMemo(() => {
    const cols = colunasPorTipo[tipoFunil] ?? [];
    const cont = new Map<string, number>();
    registros
      .filter(r => r.tipo === tipoFunil)
      .forEach(r => cont.set(r.coluna, (cont.get(r.coluna) ?? 0) + 1));
    const max = Math.max(1, ...cols.map(c => cont.get(c) ?? 0));
    return cols.map(c => ({
      coluna: c,
      n: cont.get(c) ?? 0,
      pct: ((cont.get(c) ?? 0) / max) * 100,
    }));
  }, [registros, colunasPorTipo, tipoFunil]);

  // Panorama de SLA nos 4 níveis da régua + lista dos que estão em risco (2 e 3).
  //
  // Os níveis saem de `nivelRisco()`, a MESMA função que pinta o card no quadro, e os
  // rótulos/cores de `RISCO_META`. Antes esta tela tinha a régua duplicada com hex
  // próprios (#16A34A/#F59E0B/#EA580C/#DC2626) — mesmo conceito, aparência diferente
  // da do quadro. Era a 3ª cópia: a legenda já foi apontada pra cá.
  //
  // Ordem decrescente na LISTA: atrasado antes de crítico. Lista de exceção existe
  // pra ser trabalhada de cima pra baixo.
  // ⚠️ "SEM PRAZO" É UM ESTADO, NÃO UMA AUSÊNCIA.
  //
  // Antes, processo sem `expira_em` saía calado do painel (`if (!r.sla) return`). Com 8
  // de 9 processos sem prazo, a tela anunciava "Em risco: 0 · Nenhum processo em risco"
  // — número correto, leitura falsa: não é que esteja tudo bem, é que quase nada está
  // sendo medido. Agora ele tem balde próprio, cor neutra e aparece no total.
  const sla = useMemo(() => {
    const porNivel: Record<NivelRisco, number> = { 0: 0, 1: 0, 2: 0, 3: 0 };
    let semPrazo = 0;
    const emRisco: { registro: Registro; nivel: NivelRisco }[] = [];
    // Só o que ainda corre contra o prazo — o da última etapa já foi entregue.
    const emCurso = registros.filter(r => !naUltimaEtapa(r));
    emCurso.forEach(r => {
      if (!r.sla) {
        semPrazo += 1;
        return;
      }
      const n = nivelRisco(r.sla);
      porNivel[n] += 1;
      if (n >= 2) emRisco.push({ registro: r, nivel: n });
    });
    emRisco.sort((a, b) => b.nivel - a.nivel);
    return { porNivel, semPrazo, emRisco, total: emCurso.length };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registros, colunasPorTipo]);

  // Faixas da barra de prazo. "Sem prazo" vem por último e LISTRADO: não é um grau da
  // régua, é ausência de medição — e listrado é a convenção de "sem dado". Sem isso ele
  // seria mais um cinza ao lado do cinza-quente do "No prazo", e os dois se confundiriam
  // justamente onde a distinção importa.
  const faixasSla = [
    ...([0, 1, 2, 3] as NivelRisco[]).map(n => ({
      rotulo: RISCO_META[n].rotulo,
      cor: RISCO_META[n].accent,
      n: sla.porNivel[n],
      vazado: false,
    })),
    { rotulo: 'Sem prazo', cor: '#B6C2CF', n: sla.semPrazo, vazado: true },
  ];

  // Onde está a pendência, somando os três tipos. É o único corte que a visão geral pode dar
  // e o quadro não: cada aba mostra um tipo por vez, então "quanto está parado comigo no
  // total" não existe em lugar nenhum hoje.
  const porQuem = useMemo(() => {
    const cont: Record<QuemDeve, number> = {
      equipe: 0,
      cliente: 0,
      orgao: 0,
      nenhum: 0,
      indefinido: 0,
    };
    registros.forEach(r => {
      cont[r.quem] += 1;
    });
    return cont;
  }, [registros]);

  // Por junta (multi-junta).
  const porJunta = useMemo(() => {
    const cont = new Map<string, number>();
    registros.forEach(r => cont.set(r.junta || '—', (cont.get(r.junta || '—') ?? 0) + 1));
    return Array.from(cont.entries()).sort((a, b) => b[1] - a[1]);
  }, [registros]);

  const carregando =
    loadingProj ||
    loadingLegs ||
    boardConst.isLoading ||
    boardAlt.isLoading ||
    boardBaixa.isLoading;

  if (carregando) {
    return (
      <Flex justify="center" py={20}>
        <Spinner size="xl" color="purple.500" thickness="3px" />
      </Flex>
    );
  }

  return (
    <Stack spacing={5}>
      {/* KPIs cross-tipo */}
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
        <KpiCard
          valor={kpis.andamento}
          label="Em andamento"
          accent="#1B88F5"
          icon={ClockClockwise}
          delay={0}
        />
        {/* "Em etapa de espera", NÃO "aguard. órgão".
            Este KPI conta processos numa etapa cujo fluxo é de espera externa
            (`fluxoDaEtapa(...).includes('wait')`) — ele apura ONDE o processo está.
            Já `proximaAcao` só devolve `orgao` quando NÃO há pendência do operador; com
            item de checklist em aberto, a bola é de casa mesmo numa etapa de espera.
            Os dois estão certos e medem coisas diferentes, mas com o rótulo antigo a
            tela mostrava "Aguard. órgão 3" ao lado de "Órgão 0" e parecia defeito.
            O nome novo fala de etapa; quem deve continua sendo assunto do painel. */}
        <KpiCard
          valor={kpis.aguardando}
          label="Em etapa de espera"
          accent="#F59E0B"
          icon={HourglassMedium}
          delay={0.05}
          nota={
            kpis.esperaComPendenciaPropria > 0
              ? `${kpis.esperaComPendenciaPropria} ainda com pendência da equipe`
              : undefined
          }
        />
        <KpiCard
          valor={kpis.concluidos}
          label="Concluídos"
          accent="#10B981"
          icon={CheckCircle}
          delay={0.1}
        />
        {/* A ressalva é parte do número: "0 em risco" com quase tudo sem prazo medido
            não é boa notícia, é falta de radar. */}
        <KpiCard
          valor={kpis.emRisco}
          label="Em risco (SLA)"
          accent={RISCO_META[3].accent}
          icon={WarningCircle}
          delay={0.15}
          nota={sla.semPrazo > 0 ? `${sla.semPrazo} sem prazo definido` : undefined}
        />
      </SimpleGrid>

      {/* `alignItems="start"`: sem isso a grade estica os dois cards à altura do mais
          alto, e o de prazo — que é curto quando não há risco — virava 300px de vazio.
          Card baixo ao lado de card alto é honesto; card esticado e oco parece quebrado. */}
      {/* DUAS COLUNAS QUE FLUEM SOZINHAS, nao duas linhas.
          A grade anterior era por LINHA: a altura de cada linha vinha do card mais
          alto dela, e o painel de prazo — curto quando nao ha risco — deixava ~200px
          de vazio ao lado do funil. `alignItems="start"` tirou o card esticado e oco,
          mas nao o buraco: linha e linha.
          Empilhados por COLUNA, cada lado corre com a propria altura e o vazio fecha
          sem mexer em conteudo. Esquerda = volume (funil + onde esta a pendencia);
          direita = risco e distribuicao (prazo + estados). */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4} alignItems="start">
        <Stack spacing={4}>
          {/* Funil por etapa */}
          <GlassCard intensity="medium" borderRadius="2xl" p={5}>
            <Flex align="center" justify="space-between" mb={4} wrap="wrap" gap={2}>
              <Text fontSize="13px" fontWeight="700" color="gray.800">
                Processos por etapa
              </Text>
              <Flex bg="rgba(15,23,42,0.04)" borderRadius="full" p="3px" gap="2px">
                {TIPOS_LEGALIZACAO.map(t => {
                  const on = tipoFunil === t;
                  return (
                    <Box
                      key={t}
                      as="button"
                      px={2.5}
                      h="24px"
                      borderRadius="full"
                      fontSize="11px"
                      fontWeight="700"
                      bg={on ? COR_TIPO[t].solid : 'transparent'}
                      color={on ? 'white' : 'gray.500'}
                      onClick={() => setTipoFunil(t)}
                    >
                      {TIPO_LEGALIZACAO_LABEL[t]}
                    </Box>
                  );
                })}
              </Flex>
            </Flex>
            {/* Etapa vazia continua LISTADA — a forma do funil depende de ver o buraco —
              mas esmaecida, e sem o trilho cinza desenhado. Com 5 de 8 etapas em zero,
              oito trilhos idênticos competiam com as três linhas que têm volume. */}
            <Stack spacing={2.5}>
              {funil.map(f => (
                <Box key={f.coluna} opacity={f.n === 0 ? 0.45 : 1}>
                  <Flex justify="space-between" mb={1}>
                    <Text fontSize="11px" color="gray.600" noOfLines={1}>
                      {f.coluna}
                    </Text>
                    <Text
                      fontSize="11px"
                      fontWeight="700"
                      color={f.n === 0 ? 'gray.400' : 'gray.700'}
                      sx={{ fontVariantNumeric: 'tabular-nums' }}
                    >
                      {f.n}
                    </Text>
                  </Flex>
                  <Box
                    h="6px"
                    bg={f.n === 0 ? 'transparent' : 'gray.100'}
                    borderRadius="full"
                    overflow="hidden"
                    borderWidth={f.n === 0 ? '1px' : 0}
                    borderColor="gray.100"
                  >
                    <Box
                      h="full"
                      w={`${f.pct}%`}
                      bg={COR_TIPO[tipoFunil].solid}
                      borderRadius="full"
                    />
                  </Box>
                </Box>
              ))}
              {/* Fora do quadro, do tipo SELECIONADO, como últimas linhas do funil:
                finalizados (entregues) e arquivados (desistência). Segue o seletor de
                cima — nada de repetir os nomes dos tipos, que era o que fazia os badges
                parecerem um 2º seletor. Sem barra de progresso: não é etapa. */}
              {foraDoQuadro.length > 0 && (
                <Box pt={1}>
                  <Box borderTopWidth="1px" borderStyle="dashed" borderColor="gray.200" mb={2.5} />
                  <Stack spacing={1.5}>
                    {foraDoQuadro.map(l => (
                      <Flex key={l.rotulo} justify="space-between" align="center" gap={2}>
                        <Flex align="center" gap={1.5} color="gray.400" minW={0}>
                          {l.icone}
                          <Text fontSize="11px" noOfLines={1}>
                            {l.rotulo}{' '}
                            <Text as="span" color="gray.300">
                              (fora do quadro)
                            </Text>
                          </Text>
                        </Flex>
                        <Text fontSize="11px" fontWeight="700" color="gray.400">
                          {l.n}
                        </Text>
                      </Flex>
                    ))}
                  </Stack>
                </Box>
              )}

              {/* O vazio precisa saber de quem saiu do quadro: dizer "sem processos" com
                1 arquivado faria o operador achar que o dado sumiu. */}
              {funil.every(f => f.n === 0) && (
                <Text fontSize="11px" color="gray.400" fontStyle="italic">
                  {foraDoQuadro.length > 0
                    ? `Sem processos ativos — ${foraDoQuadro
                        .map(l => `${l.n} ${l.rotulo.toLowerCase()}`)
                        .join(' · ')}.`
                    : 'Sem processos deste tipo.'}
                </Text>
              )}
            </Stack>
          </GlassCard>

          {/* DE QUEM É A BOLA — o corte que só a visão geral pode dar.
            Cada aba do quadro mostra um tipo por vez, então "quanto está parado comigo
            somando constituição, alteração e baixa" não existia em lugar nenhum.
            Ocupa a metade que era do placeholder "Automações · Fase 2", que gastava meia
            fileira pra dizer "em breve". */}
          <GlassCard intensity="medium" borderRadius="2xl" p={5}>
            <Flex align="center" gap={2} mb={4}>
              <HandPalm size={16} color="#7C3AED" weight="duotone" />
              <Text fontSize="13px" fontWeight="700" color="gray.800">
                Onde está a pendência
              </Text>
            </Flex>
            <Stack spacing={2.5}>
              {(['equipe', 'cliente', 'orgao', 'nenhum', 'indefinido'] as QuemDeve[])
                // Os três primeiros aparecem sempre (a ausência é informação: "nada
                // esperando cliente" é um fato). "Pronto" e "sem etapa" só quando existem —
                // fixos, seriam duas linhas zeradas permanentes.
                .filter(q => q === 'equipe' || q === 'cliente' || q === 'orgao' || porQuem[q])
                .map(q => {
                  const meta = QUEM_META[q];
                  const rotulo =
                    q === 'indefinido' ? 'Sem etapa configurada' : capitalizar(meta.rotulo);
                  return (
                    <Box key={q}>
                      <Flex align="center" justify="space-between" mb={1} gap={2}>
                        <Flex align="center" gap={2} minW={0}>
                          <Box w="8px" h="8px" borderRadius="full" bg={meta.cor} flexShrink={0} />
                          <Text fontSize="13px" color="gray.700" fontWeight="500" noOfLines={1}>
                            {rotulo}
                          </Text>
                        </Flex>
                        <Text
                          fontSize="11px"
                          fontWeight="700"
                          color={porQuem[q] > 0 ? 'gray.700' : 'gray.400'}
                          sx={{ fontVariantNumeric: 'tabular-nums' }}
                        >
                          {porQuem[q]}
                        </Text>
                      </Flex>
                      <Box h="6px" bg="gray.100" borderRadius="full" overflow="hidden">
                        <Box
                          h="full"
                          w={`${(porQuem[q] / Math.max(1, registros.length)) * 100}%`}
                          bg={meta.cor}
                          borderRadius="full"
                        />
                      </Box>
                    </Box>
                  );
                })}
              {registros.length === 0 && (
                <Text fontSize="11px" color="gray.400" fontStyle="italic">
                  Sem processos ativos.
                </Text>
              )}
            </Stack>
          </GlassCard>
        </Stack>
        <Stack spacing={4}>
          {/* SLA panorama + atrasados */}
          <GlassCard intensity="medium" borderRadius="2xl" p={5}>
            <Text fontSize="13px" fontWeight="700" color="gray.800" mb={4}>
              Situação de prazo (SLA)
            </Text>
            {/* BARRA SEGMENTADA, não mais quatro (agora cinco) quadradinhos numerados.
              Duas razões: a tela já tinha uma fileira de quatro números no topo, e
              repetir a mesma gramática logo abaixo fazia os dois grupos competirem; e
              com "sem prazo" entrando, cinco caixas não cabem sem espremer.
              A barra ainda diz de cara o que as caixas não diziam — a PROPORÇÃO. Com 8
              de 9 sem prazo, o cinza toma a barra inteira, e isso é a informação. */}
            <Flex h="10px" borderRadius="full" overflow="hidden" bg="gray.100" mb={3}>
              {faixasSla.map(f => (
                <Box
                  key={f.rotulo}
                  w={`${(f.n / Math.max(1, sla.total)) * 100}%`}
                  bg={f.cor}
                  sx={
                    f.vazado
                      ? {
                          backgroundImage:
                            'repeating-linear-gradient(45deg, rgba(255,255,255,.6) 0 3px, transparent 3px 6px)',
                        }
                      : undefined
                  }
                />
              ))}
            </Flex>
            <Flex wrap="wrap" columnGap={4} rowGap={1.5} mb={4}>
              {faixasSla.map(f => (
                <Flex key={f.rotulo} align="center" gap={1.5}>
                  <Box
                    w="8px"
                    h="8px"
                    borderRadius="full"
                    bg={f.vazado ? 'transparent' : f.cor}
                    borderWidth={f.vazado ? '2px' : 0}
                    borderColor={f.cor}
                    flexShrink={0}
                  />
                  <Text fontSize="11px" color="gray.600">
                    {f.rotulo}
                  </Text>
                  <Text
                    fontSize="11px"
                    fontWeight="700"
                    color={f.n > 0 ? 'gray.700' : 'gray.400'}
                    sx={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {f.n}
                  </Text>
                </Flex>
              ))}
            </Flex>
            {sla.semPrazo > 0 && (
              <Flex
                align="flex-start"
                gap={2}
                mb={4}
                px={3}
                py={2}
                borderRadius="lg"
                bg="rgba(15,23,42,0.03)"
              >
                <Box color="gray.400" flexShrink={0} pt="1px">
                  <WarningCircle size={13} weight="fill" />
                </Box>
                <Text fontSize="11px" color="gray.600" lineHeight="1.5">
                  <Text as="span" fontWeight="700" color="gray.700">
                    {sla.semPrazo} de {sla.total}
                  </Text>{' '}
                  {sla.semPrazo === 1 ? 'processo está' : 'processos estão'} sem prazo definido —{' '}
                  {sla.semPrazo === 1 ? 'fica' : 'ficam'} fora do termômetro e não{' '}
                  {sla.semPrazo === 1 ? 'entra' : 'entram'} na conta de risco.
                </Text>
              </Flex>
            )}
            <Flex align="center" gap={2} mb={2}>
              <Text
                fontSize="10px"
                fontWeight="700"
                color="gray.700"
                textTransform="uppercase"
                letterSpacing="0.06em"
              >
                Em risco agora
              </Text>
              {/* A contagem no cabeçalho é o antídoto contra a lista parecer completa
                quando ela rola. Antes vinham 8 itens de um `.slice(0, 8)` mudo: com
                30 em risco a tela mostrava 8 e não dizia que havia 22 escondidos. */}
              {sla.emRisco.length > 0 && (
                <Text fontSize="10px" fontWeight="700" color="gray.500">
                  {sla.emRisco.length}
                </Text>
              )}
            </Flex>
            {sla.emRisco.length === 0 ? (
              // Linha discreta, não bloco: logo acima está o aviso de que a maioria não é
              // medida, e um "tudo certo" em destaque ao lado dele soa como conclusão que
              // o dado não sustenta.
              <Flex align="center" gap={1.5}>
                <Box color="green.500" flexShrink={0}>
                  <CheckCircle size={13} weight="fill" />
                </Box>
                <Text fontSize="11px" color="gray.500">
                  Nenhum processo em risco entre os que têm prazo.
                </Text>
              </Flex>
            ) : (
              <Stack
                spacing={0.5}
                maxH="188px"
                overflowY="auto"
                // Some tudo na lista; a altura é que é limitada. O `pr` reserva a calha
                // da barra pra ela não cobrir o SlaBadge da direita.
                pr={sla.emRisco.length > 6 ? 1.5 : 0}
                mr={-1}
                sx={{
                  '&::-webkit-scrollbar': { width: '5px' },
                  '&::-webkit-scrollbar-thumb': {
                    background: 'rgba(15,23,42,.16)',
                    borderRadius: '999px',
                  },
                }}
              >
                {sla.emRisco.map(({ registro: r, nivel }) => (
                  <Flex
                    key={r.id}
                    as={onAbrirProcesso ? 'button' : undefined}
                    type={onAbrirProcesso ? 'button' : undefined}
                    onClick={onAbrirProcesso ? () => onAbrirProcesso(r.id, r.tipo) : undefined}
                    align="center"
                    gap={2}
                    w="full"
                    textAlign="left"
                    px={1.5}
                    py={1}
                    borderRadius="md"
                    transition="background .12s"
                    _hover={onAbrirProcesso ? { bg: 'rgba(15,23,42,.04)' } : undefined}
                    _focusVisible={{ outline: '2px solid #7C3AED', outlineOffset: '1px' }}
                  >
                    {/* Marca de nível na cor do termômetro (a mesma da barra das colunas). */}
                    <Box
                      w="3px"
                      h="16px"
                      borderRadius="full"
                      bg={RISCO_META[nivel].accent}
                      flexShrink={0}
                    />
                    <Badge colorScheme={COR_TIPO[r.tipo].scheme} fontSize="10px" borderRadius="sm">
                      {TIPO_LEGALIZACAO_LABEL[r.tipo]}
                    </Badge>
                    <Text fontSize="13px" color="gray.700" fontWeight="500" noOfLines={1} flex={1}>
                      {r.titulo}
                    </Text>
                    <Text fontSize="11px" color="gray.500" noOfLines={1}>
                      {r.coluna}
                    </Text>
                    {r.sla && <SlaBadge sla={r.sla} rotuloCurto />}
                  </Flex>
                ))}
              </Stack>
            )}
          </GlassCard>

          {/* "Por estado", não "por junta" — o recorte se chama ESTADO no filtro do
            quadro, e a lista aqui é de UFs. Dois nomes para o mesmo eixo era a mesma
            divergência de vocabulário que motivou trocar "Atrasados" por "Em risco".
            A sigla da junta segue no card e no cabeçalho do processo, onde ela é o nome
            do órgão, não um recorte. */}
          <GlassCard intensity="medium" borderRadius="2xl" p={5}>
            <Flex align="center" gap={2} mb={4}>
              <MapPin size={16} color="#7C3AED" weight="duotone" />
              <Text fontSize="13px" fontWeight="700" color="gray.800">
                Por estado (UF)
              </Text>
            </Flex>
            <Stack spacing={3}>
              {porJunta.map(([uf, n]) => (
                <Box key={uf}>
                  <Flex align="center" justify="space-between" mb={1}>
                    <Text fontSize="13px" color="gray.600" fontWeight="500">
                      {uf}
                    </Text>
                    <Badge colorScheme="purple" borderRadius="full" px={2}>
                      {n}
                    </Badge>
                  </Flex>
                  <Box h="6px" bg="gray.100" borderRadius="full" overflow="hidden">
                    <Box
                      h="full"
                      w={`${(n / (porJunta[0][1] || 1)) * 100}%`}
                      bg="#7C3AED"
                      borderRadius="full"
                    />
                  </Box>
                </Box>
              ))}
              {porJunta.length === 0 && (
                <Text fontSize="11px" color="gray.400" fontStyle="italic">
                  Sem processos.
                </Text>
              )}
            </Stack>
          </GlassCard>
        </Stack>
      </SimpleGrid>

      {/* Fase 2 (RPAs) — virou RODAPÉ. Era um card de meia fileira anunciando "em
          breve": ocupava o mesmo espaço de um painel com dado real pra dizer que ainda
          não tem dado. O aviso continua, do tamanho que ele merece. */}
      <Flex align="center" gap={2} pt={1} opacity={0.75} wrap="wrap">
        <Robot size={14} color="#0D9488" weight="duotone" />
        <Text fontSize="11px" color="gray.500">
          Acompanhamento das automações (viabilidade, protocolo e certidões) entra na Fase 2 — o
          status virá direto do monitoramento, sem depender da Análise de Bots.
        </Text>
      </Flex>
    </Stack>
  );
};

export default LegalizacaoVisaoGeral;
